import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Bell } from "@phosphor-icons/react";
import { api } from "./api";
import { useAuth } from "./auth";

const NotifCtx = createContext(null);

// ==================== Pure helpers ====================

function warnDev(prefix, err) {
  if (process.env.NODE_ENV !== "production") console.warn(prefix, err);
}

/** True se la notifica punta alla pagina che sto già guardando (evita rumore). */
function isNotifOnCurrentPage(url, locRef) {
  if (!url || url === "/" || !url.startsWith("/")) return false;
  const { pathname, search } = locRef.current || {};
  const currentFull = (pathname || "") + (search || "");
  return (
    url === currentFull ||
    url === pathname ||
    url.startsWith(pathname + "?") ||
    url.startsWith(pathname + "/")
  );
}

/** Se l'utente è un professional, il link della notifica NON deve mai puntare
 * alla bacheca pratiche (`/board`) — i pro non hanno accesso a quella pagina.
 * Se possibile ridirigiamo alla scheda pratica (`/practices/{practice_id}`),
 * altrimenti alla dashboard. Nota: le notifiche backend usano il campo
 * `practice_id` (vedi `deps.notify`), NON `entity_id`. */
function sanitizeUrlForUser(url, practiceId, userRole) {
  if (!url) return url;
  if (userRole !== "professional") return url;
  if (url.startsWith("/board")) {
    return practiceId ? `/practices/${practiceId}` : "/dashboard";
  }
  return url;
}

/** Mostra il toast per una nuova notifica (skip se già in pagina target). */
function showToastForNotification(n, locRef, nav, userRole) {
  const url = sanitizeUrlForUser(n.target_url || "", n.practice_id, userRole);
  const validUrl = url && url !== "/" && url.startsWith("/");
  if (isNotifOnCurrentPage(url, locRef)) return;
  toast(n.title, {
    description: n.body?.slice(0, 120),
    duration: 6000,
    icon: <Bell size={18} weight="duotone" className="text-purple-700" />,
    action: validUrl ? { label: "Apri", onClick: () => nav(url) } : undefined,
  });
}

/** Notifica desktop nativa (solo quando il tab non è focused/visible). */
function showDesktopNotification(n, nav, userRole) {
  if (typeof window === "undefined" || !("Notification" in window)) return;
  if (Notification.permission !== "granted") return;
  if (document.visibilityState === "visible" && document.hasFocus()) return;
  try {
    const url = sanitizeUrlForUser(n.target_url || "", n.practice_id, userRole);
    const validUrl = url && url !== "/" && url.startsWith("/");
    const notif = new Notification(n.title, {
      body: (n.body || "").slice(0, 200),
      icon: "/logo.png",
      badge: "/logo.png",
      tag: `ht-${n._id}`,
      renotify: false,
    });
    notif.onclick = () => {
      window.focus();
      if (validUrl) nav(url);
      notif.close();
    };
  } catch (err) {
    warnDev("[notifications] Desktop notif failed:", err);
  }
}

/** Diffing seenIds: ritorna array di notifiche non ancora viste E non lette. */
function pickFreshNotifications(list, seenIds) {
  return list.filter((i) => !i.is_read && !seenIds.has(i._id));
}

// ==================== Provider ====================

export const NotificationsProvider = ({ children }) => {
  const { user } = useAuth();
  const [summary, setSummary] = useState({ total: 0, per_page: {}, per_url: {} });
  // Iter93: pallini rossi immobili + cartelle di servizio
  const [propertySummary, setPropertySummary] = useState({ total: 0, per_property: {}, per_folder: {} });
  const [items, setItems] = useState([]);
  const seenIdsRef = useRef(null); // null = primo caricamento, no toast
  const nav = useNavigate();
  const location = useLocation();
  const locRef = useRef({ pathname: "", search: "" });

  useEffect(() => {
    locRef.current = { pathname: location.pathname, search: location.search };
  }, [location.pathname, location.search]);

  // Handler unico per nuovi items: toast + desktop notif + seen tracking
  const handleNewItems = useCallback((list) => {
    if (!Array.isArray(list)) return;
    if (seenIdsRef.current === null) {
      // Primo load: registra tutti, non toastare nulla
      seenIdsRef.current = new Set(list.map((i) => i._id));
      return;
    }
    const seen = seenIdsRef.current;
    const fresh = pickFreshNotifications(list, seen);
    const userRole = user?.role;
    fresh.forEach((n) => {
      seen.add(n._id);
      showToastForNotification(n, locRef, nav, userRole);
      showDesktopNotification(n, nav, userRole);
    });
    // Aggiungi anche i letti già in lista per non toastarli se riappaiono
    list.filter((i) => i.is_read).forEach((i) => seen.add(i._id));
  }, [nav, user?.role]);

  const refresh = useCallback(async () => {
    if (!user) return;
    try {
      const [s, i, ps] = await Promise.all([
        api.get("/notifications/summary"),
        api.get("/notifications", { params: { limit: 30 } }),
        api.get("/notifications/property-summary"),
      ]);
      setSummary(s.data);
      setItems(i.data);
      setPropertySummary(ps.data || { total: 0, per_property: {}, per_folder: {} });
      handleNewItems(i.data);
    } catch (e) {
      warnDev("[notifications] refresh failed:", e);
    }
  }, [user, handleNewItems]);

  // ---- SSE stream + fallback polling ----
  useEffect(() => {
    if (!user) {
      seenIdsRef.current = null;
      return undefined;
    }
    refresh();
    const cleanup = subscribeToNotificationStream({
      onSummary: (data) => {
        setSummary(data);
        // Iter93: refresh anche property_summary quando arriva una nuova notifica
        api.get("/notifications/property-summary")
          .then(r => setPropertySummary(r.data || { total: 0, per_property: {}, per_folder: {} }))
          .catch(() => {});
        api.get("/notifications", { params: { limit: 30 } })
          .then((r) => { setItems(r.data); handleNewItems(r.data); })
          .catch(() => {});
      },
      onFallbackPoll: refresh,
    });
    return cleanup;
  }, [user, refresh, handleNewItems]);

  const markRead = useCallback(async ({ ids, target_key, target_url, target_url_prefix, all, practice_id, property_id, folder_id }) => {
    await api.post("/notifications/mark-read", { ids, target_key, target_url, target_url_prefix, all, practice_id, property_id, folder_id });
    refresh();
  }, [refresh]);

  // ---- Auto-mark su navigazione fine (Iter97/Iter102) ----
  // Strategy: NON marchiamo automaticamente su cambio pagina generico.
  // Marchiamo SOLO quando l'utente raggiunge la sezione finale:
  //   • Notifica su messaggio pratica → auto-mark quando `PracticeDetail`
  //     monta (l'utente sta guardando la chat della pratica).
  //   • Notifica consegna documento → auto-mark quando l'utente ENTRA nella
  //     specifica cartella dell'immobile (click su Folder in `PropertyDetail`
  //     o `PropertyDocumentsSection`).
  //   • Iter102: quando l'utente entra in /properties (lista Immobili), le
  //     notifiche con `target_key='properties'` vengono azzerate a livello
  //     macro (badge sidebar) via `Layout.jsx`. I badge per singola cartella
  //     restano finché l'utente non apre la cartella specifica.
  // I componenti target chiamano direttamente `markRead({...})` con il
  // corretto identificatore (`practice_id` / `folder_id` / `target_key`).

  // ---- Live counter in tab title ----
  useEffect(() => {
    const base = "Housetag.it — Real Estate Suite";
    if (!user) { document.title = base; return; }
    const total = summary?.total || 0;
    document.title = total > 0 ? `(${total > 99 ? "99+" : total}) ${base}` : base;
  }, [user, summary?.total]);

  const countForUrl = useCallback((url) => {
    if (!url) return 0;
    const perUrl = summary?.per_url || {};
    let count = perUrl[url] || 0;
    for (const [k, v] of Object.entries(perUrl)) {
      if (k !== url && (k.startsWith(url + "?") || k.startsWith(url + "/") || k.startsWith(url + "#"))) {
        count += v;
      }
    }
    return count;
  }, [summary]);

  const requestDesktopPermission = useCallback(async () => {
    if (typeof window === "undefined" || !("Notification" in window)) {
      return { supported: false, permission: "unsupported" };
    }
    if (Notification.permission === "granted") {
      return { supported: true, permission: "granted" };
    }
    try {
      const p = await Notification.requestPermission();
      return { supported: true, permission: p };
    } catch (e) {
      return { supported: true, permission: "default", error: e };
    }
  }, []);

  return (
    <NotifCtx.Provider value={{ summary, propertySummary, items, refresh, markRead, countForUrl, requestDesktopPermission }}>
      {children}
    </NotifCtx.Provider>
  );
};

/**
 * Sottoscrive lo stream SSE `/notifications/stream` (EventSource); su errore
 * chiude la connessione e fa fallback su polling 20s.
 * Ritorna una funzione di cleanup idempotente.
 */
function subscribeToNotificationStream({ onSummary, onFallbackPoll }) {
  const token = localStorage.getItem("ht_token");
  const backend = process.env.REACT_APP_BACKEND_URL;
  let es;
  let pollTimer;

  const startPolling = () => {
    if (pollTimer) return;
    pollTimer = setInterval(onFallbackPoll, 20000);
  };

  const canUseSSE = token && backend && typeof EventSource !== "undefined";
  if (canUseSSE) {
    try {
      es = new EventSource(`${backend}/api/notifications/stream?token=${encodeURIComponent(token)}`);
      es.addEventListener("notif", (e) => {
        try {
          const data = JSON.parse(e.data);
          onSummary(data);
        } catch (err) {
          warnDev("[notifications] parse SSE:", err);
        }
      });
      es.onerror = (err) => {
        warnDev("[notifications] SSE error, falling back to polling:", err);
        es?.close();
        startPolling();
      };
    } catch (e) {
      warnDev("[notifications] SSE setup failed:", e);
      startPolling();
    }
  } else {
    startPolling();
  }

  return () => {
    es?.close();
    if (pollTimer) clearInterval(pollTimer);
  };
}

export const useNotifications = () => useContext(NotifCtx) || {
  summary: { total: 0, per_page: {}, per_url: {} },
  propertySummary: { total: 0, per_property: {}, per_folder: {} },
  items: [], refresh: () => {}, markRead: () => {}, countForUrl: () => 0,
  requestDesktopPermission: async () => ({ supported: false, permission: "unsupported" }),
};
